<script setup lang="ts">
import { ref } from "vue";
import { ErrorResponse } from "../../interface/ErrorInterface";
import ValidationErrorComponent from "../../components/ValidationErrorComponent.vue";
import {
  ResourceUpload,
  ResourceType,
} from "../../interface/ResourcesInterface";
import { appContainer } from "../../container";
import { ResourceService } from "../../services/resourceService";
import { LoadingService } from "../../services/loadingService";
import { ToastService } from "../../services/toastService";
import { AxiosError } from "axios";

const resourceService = appContainer.resolve(ResourceService);
const loadingService = appContainer.resolve(LoadingService);
const toastService = appContainer.resolve(ToastService);

const emit = defineEmits(["close", "fileUploaded"]);

const resourceTypes = ResourceType;
const selectedResourceType = ref<ResourceType | "">("");
const errors = ref<Record<string, string>>({});
const validationErrors = ref<ErrorResponse>();

const YOUTUBE_STANDARD_URL = "youtube.com/watch?v=";
const YOUTUBE_SHORT_URL = "youtu.be/";

const formData = ref<ResourceUpload>({
  name: "",
  original_name: "",
  description: "",
  file: undefined,
  link: "",
});

const handleFileUpload = (event: Event) => {
  if (selectedResourceType.value === ResourceType.Pdf) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        errors.value.file = "Only PDF files are allowed.";
        (event.target as HTMLInputElement).value = "";
        return;
      }

      formData.value = {
        ...formData.value,
        file,
        original_name: file.name,
      };
    }
  }
};

const isValidYoutubeUrl = (url: string): boolean => {
  return url.includes(YOUTUBE_STANDARD_URL) || url.includes(YOUTUBE_SHORT_URL);
};

const getYoutubeId = (url: string): string => {
  if (url.includes(YOUTUBE_STANDARD_URL)) {
    const parts = url.split(YOUTUBE_STANDARD_URL);
    return parts.length > 1 ? parts[1].split("&")[0] : "";
  }
  if (url.includes(YOUTUBE_SHORT_URL)) {
    const parts = url.split(YOUTUBE_SHORT_URL);
    return parts.length > 1 ? parts[1].split("?")[0] : "";
  }
  return "";
};

const validateForm = (): boolean => {
  errors.value = {};
  let isValid = true;

  if (!formData.value.name?.trim()) {
    errors.value.name = "Resource Name is required.";
    isValid = false;
  }

  if (!formData.value.description?.trim()) {
    errors.value.description = "Description is required.";
    isValid = false;
  }

  if (!selectedResourceType.value) {
    errors.value.resourceType = "Resource Type is required.";
    isValid = false;
  }

  if (selectedResourceType.value === ResourceType.Pdf && !formData.value.file) {
    errors.value.file = "Please upload a PDF file.";
    isValid = false;
  }

  if (selectedResourceType.value === ResourceType.YTVideo) {
    if (!formData.value.link?.trim()) {
      errors.value.link = "Video URL is required.";
      isValid = false;
    } else if (formData.value.link && !isValidYoutubeUrl(formData.value.link)) {
      errors.value.link = "Please enter a valid YouTube URL";
      isValid = false;
    }
  }

  return isValid;
};

const addResources = async () => {
  if (!validateForm()) return;

  if (selectedResourceType.value === ResourceType.YTVideo) {
    const videoId = getYoutubeId(formData.value.link!);
    formData.value.link = videoId;
  }

  const loader = await loadingService.show();

  try {
    if (selectedResourceType.value === ResourceType.Pdf) {
      await resourceService.addResource(formData.value);
    } else {
      await resourceService.addResourceYt(formData.value);
    }

    toastService.success("Resource Added Successfully");
    emit("fileUploaded");
  } catch (err) {
    const error = err as AxiosError;
    const errorResponse = error.response?.data as ErrorResponse;

    if (errorResponse?.errors) {
      const errorMessages = Object.values(errorResponse.errors)
        .flat()
        .join("<br>");
      toastService.error(errorMessages);
    } else {
      toastService.error("Failed to add resource");
    }
  } finally {
    loader.hide();
  }
};
</script>

<template>
  <div class="modal-backdrop">
    <div class="modal fade show" id="edit-modal">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <p class="modal-title fw-bold">Add Resource</p>
            <button
              type="button"
              class="btn-close"
              @click="emit('close')"
            ></button>
          </div>
          <ValidationErrorComponent
            v-if="validationErrors"
            :validations="validationErrors"
          ></ValidationErrorComponent>
          <form class="w-100 overflow-y-auto" @submit.prevent="addResources">
            <div class="modal-body">
              <div class="mb-3">
                <label for="resourceName" class="fw-bold mb-2 mt-3">Name</label>
                <input
                  type="text"
                  id="resourceName"
                  v-model="formData.name"
                  placeholder="Name"
                  maxlength="80"
                  class="form-control"
                />
                <div v-if="errors.name" class="text-danger error mt-2">
                  {{ errors.name }}
                </div>
              </div>

              <div class="mb-3">
                <label for="resourceDescription" class="fw-bold mb-2"
                  >Description</label
                >
                <textarea
                  id="resourceDescription"
                  v-model="formData.description"
                  placeholder="Description"
                  maxlength="500"
                  rows="3"
                  class="form-control"
                ></textarea>
                <div v-if="errors.description" class="text-danger error mt-2">
                  {{ errors.description }}
                </div>
              </div>

              <div class="mb-3">
                <label for="resourceType" class="form-label fw-bold"
                  >Resource Type</label
                >
                <select
                  id="resourceType"
                  class="form-select"
                  v-model="selectedResourceType"
                >
                  <option value="">Please Select</option>
                  <template v-for="resource in resourceTypes" :key="resource">
                    <option :value="resource">
                      {{ resource }}
                    </option>
                  </template>
                </select>
                <div v-if="errors.resourceType" class="text-danger error mt-2">
                  {{ errors.resourceType }}
                </div>
              </div>

              <div
                class="mb-3"
                v-if="selectedResourceType === resourceTypes.YTVideo"
              >
                <label for="resourceName" class="fw-bold mb-2">Video Url</label>
                <input
                  type="text"
                  v-model="formData.link"
                  placeholder="Video Url"
                  class="form-control"
                />
                <div v-if="errors.link" class="text-danger error mt-2">
                  {{ errors.link }}
                </div>
              </div>

              <div
                class="mb-3"
                v-if="selectedResourceType === resourceTypes.Pdf"
              >
                <label for="resource" class="fw-bold mb-2"
                  >Upload Resource</label
                >
                <input
                  type="file"
                  id="resource"
                  class="form-control"
                  accept="application/pdf"
                  @change="handleFileUpload"
                />
                <div v-if="errors.file" class="text-danger error mt-2">
                  {{ errors.file }}
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="submit"
                class="btn btn-primary btn-sm mx-auto text-white"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
